import styled from "styled-components";
import { Link } from 'react-router-dom';

import { v } from '../../styles/variables';

export const HeaderContainer = styled.div`
    /* Desktop sidebar layout - flex item in row layout */
    @media(min-width: 769px) {
        width: var(--sidebar-collapsed-width);
        height: 100vh;
        display: flex;
        flex-direction: column;
        background: ${({theme}) => theme.colors.bg2};
        box-shadow: 0 4px 30px ${({theme}) => theme.colors.shadow};
        backdrop-filter: blur(20px);
        -webkit-backdrop-filter: blur(20px);
        border-right: 1px solid rgba(255, 255, 255, 0.3);
        padding: ${v.mdSpacing} 0;
        border-radius: 0 50px 50px 0;
        overflow: hidden; /* Remove scroll - let content adjust to height */
        flex-shrink: 0; /* Prevent sidebar from shrinking */
        position: fixed;
        top: 0;
        left: 0;
        z-index: 1000;
        transition: width 0.3s ease;

        &:hover,
        &[data-more-open="true"] {
            width: var(--sidebar-expanded-width);
        }
    }

    /* Mobile bottom navigation (unchanged) */
    @media(max-width: 768px) {
        height: var(--header-height);
        display: grid;
        grid-template-columns: repeat(2, 50%);
        grid-template-rows: 1fr;
        margin: 0;
        justify-self: center;
        background: ${({theme}) => theme.colors.bg};
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 1000;
        width: 100%;
        box-sizing: border-box;
        /* Ensure proper safe area handling */
        padding-top: env(safe-area-inset-top);
        height: calc(var(--header-height) + env(safe-area-inset-top));
        /* Disable pointer events to allow touch events on elements behind */
        pointer-events: none;
    }
`;

export const LogoContainer = styled.div`
    /* Desktop sidebar layout */
    @media(min-width: 769px) {
        padding: ${v.smSpacing}; /* Reduced padding when collapsed */
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        margin-bottom: ${v.mdSpacing};
        text-align: center;
        transition: padding 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        /* Overlay both logo variants for a crossfade */
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 56px;
        
        /* Expand padding when sidebar is hovered */
        ${HeaderContainer}:hover &,
        ${HeaderContainer}[data-more-open="true"] & {
            padding: ${v.mdSpacing};
        }
        
        /* Both logos stacked in the same space so they can crossfade */
        .logo-icon,
        .logo-horizontal {
            display: block;
            margin: 0 auto;
            transition:
                opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1),
                transform 0.45s cubic-bezier(0.4, 0, 0.2, 1);
            will-change: opacity, transform;
            pointer-events: none;
        }
        
        /* Icon logo (collapsed state) - visible by default, sits in flow */
        .logo-icon {
            position: relative;
            height: 32px;
            width: auto;
            opacity: 1;
            transform: translateX(0);
        }
        
        /* Horizontal logo (expanded state) - absolutely overlaid, off to the right */
        .logo-horizontal {
            position: absolute;
            top: 50%;
            left: 50%;
            height: 40px;
            width: auto;
            opacity: 0;
            transform: translate(calc(-50% + 24px), -50%);
        }
        
        /* When sidebar expands: icon slides left out, wordmark slides in from right */
        ${HeaderContainer}:hover & .logo-icon,
        ${HeaderContainer}[data-more-open="true"] & .logo-icon {
            opacity: 0;
            transform: translateX(-24px);
        }
        
        ${HeaderContainer}:hover & .logo-horizontal,
        ${HeaderContainer}[data-more-open="true"] & .logo-horizontal {
            opacity: 1;
            transform: translate(-50%, -50%);
        }
    }

    /* Mobile layout - hide logo in header */
    @media(max-width: 768px) {
        display: none;
    }
`;

export const SidebarSearchForm = styled.form`
    @media(min-width: 769px) {
        display: flex;
        align-items: center;
        gap: 0;
        margin: 0 ${v.smSpacing} ${v.mdSpacing};
        padding: 8px;
        border-radius: 999px;
        background: ${({theme}) => theme.colors.bg3 || 'rgba(255,255,255,0.06)'};
        border: 1px solid ${({theme}) => theme.colors.border || 'rgba(255,255,255,0.08)'};
        transition: padding 0.3s ease, gap 0.3s ease, background 0.2s ease, border-color 0.2s ease;
        cursor: text;
        overflow: hidden;
        /* Center icon by default; slide it left when focused or when there's a value */
        justify-content: center;

        &:hover {
            background: ${({theme}) => theme.colors.primaryLight || 'rgba(255,255,255,0.08)'};
        }

        ${HeaderContainer}[data-expanded="true"] &:focus-within {
            justify-content: flex-start;
            border-color: ${({theme}) => theme.colors.primary};
            background: ${({theme}) => theme.colors.bg2 || 'rgba(255,255,255,0.08)'};
        }

        .search-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 20px;
            height: 20px;
            flex-shrink: 0;
            color: ${({theme}) => theme.colors.primary};
            transition: transform 0.2s ease;
        }

        input {
            flex: 0 0 0;
            min-width: 0;
            width: 0;
            opacity: 0;
            border: none;
            outline: none;
            background: transparent;
            color: ${({theme}) => theme.colors.text};
            font-size: 13px;
            padding: 0;
            transition: opacity 0.2s ease, width 0.2s ease;
            appearance: none;
            -webkit-appearance: none;

            &::placeholder {
                color: ${({theme}) => theme.colors.textSecondary};
            }

            /* Hide native search clear button (WebKit + IE/Edge legacy) */
            &::-webkit-search-cancel-button,
            &::-webkit-search-decoration,
            &::-webkit-search-results-button,
            &::-webkit-search-results-decoration {
                -webkit-appearance: none;
                appearance: none;
                display: none;
            }
            &::-ms-clear,
            &::-ms-reveal {
                display: none;
                width: 0;
                height: 0;
            }
        }

        .search-clear {
            display: none;
            align-items: center;
            justify-content: center;
            width: 20px;
            height: 20px;
            flex-shrink: 0;
            padding: 0;
            border: none;
            background: transparent;
            color: ${({theme}) => theme.colors.textSecondary};
            cursor: pointer;
            border-radius: 999px;
            transition: color 0.15s ease, background 0.15s ease;

            &:hover {
                color: ${({theme}) => theme.colors.primary};
                background: ${({theme}) => theme.colors.primaryLight || 'rgba(255,255,255,0.08)'};
            }
        }

        ${HeaderContainer}[data-expanded="true"] &:focus-within[data-has-value="true"] .search-clear {
            display: flex;
        }

        ${HeaderContainer}:hover &,
        ${HeaderContainer}[data-more-open="true"] & {
            padding: 8px 12px;
            gap: 8px;
        }

        ${HeaderContainer}:hover & input,
        ${HeaderContainer}[data-more-open="true"] & input {
            flex: 1 1 0;
            width: auto;
            opacity: 1;
        }
    }

    @media(max-width: 768px) {
        display: none;
    }
`;

export const HeaderLinkContainer = styled.div`
    /* Desktop sidebar layout */
    @media(min-width: 769px) {
        padding: 0 ${v.mdSpacing};
        flex: 1;
        display: flex;
        flex-direction: column;
        background: transparent;
        box-shadow: none;
        backdrop-filter: none;
        -webkit-backdrop-filter: none;
        border: none;
        border-radius: 0;
        margin: 0;
        overflow: hidden; /* Prevent overflow */
        min-height: 0; /* Allow flex item to shrink */
    }

    /* Mobile bottom navigation - conditionally hidden */
    @media(max-width: 768px) {
        padding: 0;
        margin: auto;
        border-radius: 50px;
        background: ${({theme}) => theme.colors.bg2};
        box-shadow: 0 4px 30px ${({theme}) => theme.colors.shadow};
        backdrop-filter: blur(20px);
        -webkit-backdrop-filter: blur(20px);
        border: 1px solid rgba(255, 255, 255, 0.3);
        position: fixed !important;
        bottom: 0;
        top: auto;
        width: 100%;
        left: 0;
        right: 0;
        margin: 0;
        border-radius: 0;
        /* Ensure proper safe area handling */
        padding-bottom: env(safe-area-inset-bottom);
        height: calc(var(--bottom-nav-height) + env(safe-area-inset-bottom));
        z-index: 1000;
        border-radius: 50px;
        
        /* Smooth slide transition for scroll-based hiding */
        transition: transform 0.3s ease;
        transform: ${({$scrollHidden}) => $scrollHidden ? 'translateY(100%)' : 'translateY(0)'};
        
        /* Route-based full hide (e.g. Messages page) */
        display: ${({$hideBottomNavigation}) => $hideBottomNavigation ? 'none' : 'block'};
    }
`;

export const HeaderIcons = styled.div`
    /* Desktop sidebar layout - vertical stack */
    @media(min-width: 769px) {
        display: flex;
        flex-direction: column;
        gap: 4px; /* Use a very small gap to fit more items */
        margin: 0;
        width: 100%;
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        overflow-x: hidden;
        justify-content: space-around;
        scrollbar-width: none;
        &::-webkit-scrollbar { width: 0; height: 0; }
        
        :hover {
            transform: none;
            transition: none;
        }

        /* Hide mobile navigation on desktop */
        .mobile-nav {
            display: none !important;
        }

        /* Show desktop navigation */
        .desktop-nav {
            display: contents !important;
        }
    }

    /* Mobile bottom navigation - 6 items evenly distributed */
    @media(max-width: 768px) {
        display: grid;
        grid-template-columns: repeat(6, 1fr); /* 6 equal columns for full width */
        grid-template-rows: 1fr;
        margin: 0;
        padding: 0;
        width: 100%;
        height: 100%;
        /* Re-enable pointer events for navigation items */
        pointer-events: auto;

        :hover {
            box-shadow: inset 0 0 0 0 ${({theme}) => theme.colors.shadow};
            transform: scale(1.1);
            transition: all .5s;
            -webkit-transition: all .5s;
            -moz-transition: all .5s;
        }

        /* Hide desktop navigation on mobile */
        .desktop-nav {
            display: none !important;
        }

        /* Show mobile navigation */
        .mobile-nav {
            display: contents !important;
        }
    }
`;

export const HeaderLink = styled(Link)`
    /* Desktop sidebar layout */
    @media(min-width: 769px) {
        background: ${({theme, $isActive}) => (!$isActive ? `transparent` : theme.colors.primary)};
        border-radius: 12px;
        padding: ${v.smSpacing}; /* Initially just padding for icon */
        margin-bottom: 2px; /* Very small margin */
        text-decoration: none;
        color: ${({theme, $isActive}) => $isActive ? theme.button.primaryText : theme.colors.primary};
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        width: 100%;
        box-sizing: border-box;
        border-left: 3px solid ${({theme, $isActive}) => $isActive ? theme.colors.primary : 'transparent'};
        flex-shrink: 0; /* Prevent items from shrinking too much */
        justify-content: flex-start;
        
        /* When sidebar is collapsed, center the icon */
        justify-content: center;
        
        /* When sidebar is hovered (expanded), show normal layout */
        ${HeaderContainer}:hover &,
        ${HeaderContainer}[data-more-open="true"] & {
            padding: ${v.smSpacing} ${v.mdSpacing};
            justify-content: flex-start;
        }
        
        &:hover {
            background: ${({theme}) => theme.colors.primaryLight};
            transform: translateX(2px);
            box-shadow: none;
        }
    }

    /* Mobile bottom navigation */
    @media(max-width: 768px) {
        background: ${({theme, $isActive}) => (!$isActive ? `transparent` : theme.colors.bg3)};
        border-radius: 50px;
        /* Re-enable pointer events for navigation links */
        pointer-events: auto;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        width: 100%;
        text-decoration: none;
        
        :hover{
            box-shadow: none;
        }
    }
`;

export const HeaderLinkIcon = styled.div`
    /* Desktop sidebar layout */
    @media(min-width: 769px) {
        padding: 0;
        margin: 0;
        margin-right: 0; /* No margin when collapsed */
        justify-self: flex-start;
        display: flex;
        align-items: center;
        transition: margin 0.3s ease;
        
        /* Add margin when sidebar is expanded */
        ${HeaderContainer}:hover &,
        ${HeaderContainer}[data-more-open="true"] & {
            margin-right: ${v.smSpacing};
        }
        
        :hover{
            box-shadow: none;
        }
        
        svg {
            font-size: 18px; /* Slightly smaller icon */
            color: ${({theme, $isActive}) => $isActive ? theme.button.primaryText : theme.colors.primary};
            transition: color 0.3s ease;
        }
        
        /* Change color to white when link is hovered */
        ${HeaderLink}:hover & svg {
            color: ${({theme}) => theme.button.primaryText};
        }
    }

    /* Mobile bottom navigation */
    @media(max-width: 768px) {
        padding: 0;
        margin: 0;
        justify-self: center;
        display: flex;
        align-items: center;
        justify-content: center;
        
        :hover{
            box-shadow: none;
        }
        svg {
            font-size: 20px;
            color: ${({theme}) => theme.colors.primary};
        }
    }
`;

export const HeaderLinkText = styled.span`
    /* Desktop sidebar layout - show text labels only on hover */
    @media(min-width: 769px) {
        font-size: 13px; /* Slightly smaller font */
        font-weight: 500;
        color: ${({theme, $isActive}) => $isActive ? theme.button.primaryText : theme.colors.primary};
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        opacity: 0;
        width: 0;
        transition: opacity 0.3s ease, width 0.3s ease, color 0.3s ease;
        
        /* Show text when sidebar is hovered */
        ${HeaderContainer}:hover &,
        ${HeaderContainer}[data-more-open="true"] & {
            opacity: 1;
            width: auto;
            display: block;
        }
        
        /* Change color to white when link is hovered */
        ${HeaderLink}:hover & {
            color: ${({theme}) => theme.button.primaryText};
        }
    }

    /* Mobile bottom navigation - hide text labels */
    @media(max-width: 768px) {
        display: none;
    }
`;

// Add a profile section for the sidebar
export const SidebarProfileSection = styled.div`
    /* Desktop sidebar layout - show user profile at bottom */
    @media(min-width: 769px) {
        margin-top: auto;
        padding: ${v.mdSpacing};
        border-top: 1px solid rgba(255, 255, 255, 0.1);
        display: flex;
        align-items: center;
        gap: 0; /* No gap when collapsed */
        justify-content: center; /* Center avatar when collapsed */
        transition: gap 0.3s ease, justify-content 0.3s ease;
        cursor: pointer;
        border-radius: 12px;
        
        /* Hover effect for clickable profile */
        &:hover {
            background: rgba(255, 255, 255, 0.05);
        }
        
        /* Expand when sidebar is hovered */
        ${HeaderContainer}:hover &,
        ${HeaderContainer}[data-more-open="true"] & {
            gap: ${v.smSpacing};
            justify-content: flex-start;
        }
        
        .avatar {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: ${({theme}) => theme.colors.primary};
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: bold;
            font-size: 16px;
            flex-shrink: 0;
        }
        
        .user-info {
            flex: 1;
            min-width: 0;
            opacity: 0;
            max-width: 0;
            overflow: hidden;
            transition: opacity 0.3s ease, max-width 0.3s ease;
            
            /* Show user info when sidebar is hovered */
            ${HeaderContainer}:hover &,
            ${HeaderContainer}[data-more-open="true"] & {
                opacity: 1;
                max-width: 130px;
            }
            
            .fullname {
                font-size: 14px;
                font-weight: 600;
                color: ${({theme}) => theme.colors.text};
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            
            .username {
                font-size: 12px;
                color: ${({theme}) => theme.colors.textSecondary};
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            
            /* Underline name/username on hover of the clickable area */
        }
        
        /* Underline text on hover of the profile clickable area */
        & > div:first-child:hover .fullname,
        & > div:first-child:hover .username {
            text-decoration: underline;
        }
        
        .logout-button {
            display: none;
            background: transparent;
            border: none;
            color: ${({theme}) => theme.colors.primary};
            cursor: pointer;
            padding: 8px;
            border-radius: 8px;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
            flex-shrink: 0;
            
            /* Show logout button when sidebar is hovered */
            ${HeaderContainer}:hover &,
            ${HeaderContainer}[data-more-open="true"] & {
                display: flex;
            }
            
            &:hover {
                background: ${({theme}) => theme.colors.secondary};
                color: ${({theme}) => theme.button.primaryText};
            }
            
            &:active {
                transform: scale(0.95);
            }
        }
    }

    /* Mobile bottom navigation - hide profile section */
    @media(max-width: 768px) {
        display: none;
    }
`;

export const STheme = styled.div`
    font-size: 16px;
    padding: 0 ${v.smSpacing};
    margin: ${v.smSpacing};
    justify-self: center;
    :hover {
        box-shadow: none;
    }
    
`;

export const SToggleThumb = styled.div`
    height: 18px;
    width: 18px;
    position: absolute;
    top: 1px;
    bottom: 1px;
    transition: .2s ease right;
    right: calc(100% - 18px -1px);
    border-radius: 50px;
    background: ${({theme}) => theme.colors.bg};
`;

